import { useEffect, useState } from 'react';

export function splitLocalDateTime(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const [date = '', rest = ''] = value.split('T');
  return { date, time: rest.slice(0, 5) };
}

export function joinLocalDateTime(date: string, time: string): string {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

export function dateInputMin(min?: string): string | undefined {
  if (!min) return undefined;
  return splitLocalDateTime(min).date || undefined;
}

export function timeInputMin(min: string | undefined, date: string): string | undefined {
  if (!min || !date) return undefined;
  const { date: minDate, time: minTime } = splitLocalDateTime(min);
  if (date !== minDate || !minTime) return undefined;
  return minTime;
}

interface DateTimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  stepMinutes?: number;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  'aria-label'?: string;
  dateAriaLabel?: string;
  timeAriaLabel?: string;
}

/** Safari-safe date + time pair that still speaks `YYYY-MM-DDTHH:MM`. */
export default function DateTimeField({
  value,
  onChange,
  min,
  stepMinutes,
  className = '',
  inputClassName = '',
  disabled,
  required,
  id,
  'aria-label': ariaLabel,
  dateAriaLabel,
  timeAriaLabel,
}: DateTimeFieldProps) {
  const [date, setDate] = useState(() => splitLocalDateTime(value).date);
  const [time, setTime] = useState(() => splitLocalDateTime(value).time);

  useEffect(() => {
    const next = splitLocalDateTime(value);
    setDate(next.date);
    setTime(next.time);
  }, [value]);

  const emit = (nextDate: string, nextTime: string) => {
    setDate(nextDate);
    setTime(nextTime);
    onChange(joinLocalDateTime(nextDate, nextTime));
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`.trim()}
      role="group"
      aria-label={ariaLabel}
    >
      <input
        id={id}
        type="date"
        value={date}
        min={dateInputMin(min)}
        disabled={disabled}
        required={required}
        aria-label={dateAriaLabel ?? (ariaLabel ? `${ariaLabel} date` : 'Date')}
        onChange={(e) => emit(e.target.value, time)}
        className={`min-w-[9.5rem] flex-1 ${inputClassName}`.trim()}
      />
      <input
        type="time"
        value={time}
        min={timeInputMin(min, date)}
        step={stepMinutes ? stepMinutes * 60 : undefined}
        disabled={disabled}
        required={required}
        aria-label={timeAriaLabel ?? (ariaLabel ? `${ariaLabel} time` : 'Time')}
        onChange={(e) => emit(date, e.target.value)}
        className={`w-[9.5rem] ${inputClassName}`.trim()}
      />
    </div>
  );
}
